import React, { useState } from "react";
import axios from "axios";
import emailjs from "@emailjs/browser";
import { PhoneCall, Clock, ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// EmailJS — same service/key as Quote form, dedicated template for callback.
const EMAILJS_SERVICE_ID = "service_jvaopxc";
const EMAILJS_TEMPLATE_ID = "template_6ib1xty";
const EMAILJS_PUBLIC_KEY = "5MdsvUkN7ABTQfSBN";

const TIMES = ["Délelőtt (9–12)", "Délután (12–17)", "Kora este (17–19)", "Bármikor"];

export default function Callback() {
  const [form, setForm] = useState({ name: "", phone: "", preferred_time: "Bármikor", note: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("A név és a telefonszám kötelező.");
      return;
    }
    setLoading(true);
    try {
      const { data: saved } = await axios.post(`${API}/callback`, form);

      // Best-effort EmailJS notification — does not block UX on failure
      try {
        await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          {
            name: saved.name || "",
            phone: saved.phone || "",
            preferred_time: saved.preferred_time || "Bármikor",
          },
          { publicKey: EMAILJS_PUBLIC_KEY }
        );
      } catch (emailErr) {
        console.warn("EmailJS callback error:", emailErr);
      }

      setSent(true);
      toast.success("Rögzítettük — 2 munkaórán belül hívjuk!");
    } catch (err) {
      toast.error("Hiba — próbálja újra.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="callback" data-testid="callback-section" className="py-20 md:py-32 bg-[#0F0F0F] text-[#FFFFFF] relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#DC2626] rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-[1.2fr,1fr] gap-12 items-start">
          <div>
            <span className="badge-chip" style={{ background: "#FFFFFF", color: "#0F0F0F" }}>
              Gyors kapcsolat
            </span>
            <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tight mt-4 leading-[0.95] mb-6">
              Nincs ideje<br/>űrlapot kitölteni?
            </h2>
            <p className="text-[#E4E4E7] text-base md:text-lg leading-relaxed max-w-xl mb-8">
              Adja meg a telefonszámát, és <strong className="text-[#FFFFFF]">2 munkaórán belül</strong> visszahívjuk. Mondja el, mire van szüksége — a többit intézzük.
            </p>

            <div className="flex flex-wrap items-center gap-8 text-sm">
              <div className="flex items-center gap-2">
                <Clock size={20} weight="duotone" className="text-[#FFFFFF]" />
                <span>2 munkaóra átlagos reakcióidő</span>
              </div>
              <div className="flex items-center gap-2">
                <PhoneCall size={20} weight="duotone" className="text-[#FFFFFF]" />
                <span>H–P: 8–18, Szo: 9–13</span>
              </div>
            </div>
          </div>

          <div className="bg-[#FFFFFF] text-[#0F0F0F] p-6 md:p-8 border border-[#FFFFFF]">
            {sent ? (
              <div data-testid="callback-success" className="text-center py-8">
                <CheckCircle size={48} weight="fill" className="text-[#0F0F0F] mx-auto mb-3" />
                <h3 className="font-display font-black text-2xl mb-2">Felvettük!</h3>
                <p className="text-[#525252] text-sm">
                  Hamarosan hívjuk a(z) <strong>{form.phone}</strong> számon.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="callback-form">
                <h3 className="font-display font-black text-2xl tracking-tight mb-4">Hívjanak vissza</h3>

                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block">Név*</label>
                    <input
                      data-testid="callback-name-input"
                      className="input-sharp"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block">Telefonszám*</label>
                    <input
                      data-testid="callback-phone-input"
                      className="input-sharp"
                      required
                      type="tel"
                      placeholder="+36 20 ..."
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block">Mikor keressük?</label>
                    <div className="flex flex-wrap gap-2">
                      {TIMES.map((t) => (
                        <button
                          key={t}
                          type="button"
                          data-testid={`time-${t}`}
                          onClick={() => setForm({ ...form, preferred_time: t })}
                          className={`px-3 py-2 text-xs font-semibold border transition-all ${
                            form.preferred_time === t
                              ? "bg-[#0F0F0F] text-[#FFFFFF] border-[#0F0F0F]"
                              : "bg-white text-[#0F0F0F] border-[#E4E4E7] hover:border-[#0F0F0F]"
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="callback-submit"
                  className="cta-accent w-full justify-center mt-6 disabled:opacity-60"
                >
                  {loading ? "Küldés..." : "Visszahívást kérek"} <ArrowRight size={18} weight="bold" />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
